import React, { useMemo } from 'react';

import ms from 'ms';
import Link from 'next/link.js';
import { styled } from '@components-shared/src/stitches';
import { getSearchPageResults } from '@components/Explore/helpers';
import { LoadingBars } from '@components/LoadingBars/LoadingBars';
import { CoverImages } from '@components/Playlists/basics/CoverImages';
import { useGlobalSearchBarInput } from '@components/SearchBar/useGlobalSearchBarInput';
import { Avatar } from '@components/basics/Images/Avatar';
import { Cover } from '@components/basics/Images/Cover';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import {
  DividerContainer,
  LoadingContainer,
  ScrollableContainer,
  SearchDivider,
  SearchResultsContainer,
  SearchRow,
} from '@components/main-components/search/SearchResults';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import {
  DefaultSearchBarResultsDocument,
  getFragment,
  gql,
  InputSearchBarResultsDocument,
  SearchBarResultArtistFragmentDoc,
  SearchBarResultCollectorFragmentDoc,
  SearchBarResultReleaseFragmentDoc,
  SearchBarResultShelfFragmentDoc,
  useQuery,
} from '@graphql/reactQuery';
import {
  SearchHistoryArtistDocument,
  SearchHistoryCollectorDocument,
  SearchHistoryPlaylistDocument,
  SearchHistoryReleaseDocument,
} from '@graphql/reactQuery';
import type {
  SearchBarResultArtistFragment,
  SearchBarResultCollectorFragment,
  SearchBarResultReleaseFragment,
  SearchBarResultShelfFragment,
} from '@graphql/reactQuery';
import { useVisualViewport } from '@hooks/useVisualViewport';
import { webappUriPath } from '@utils/url';
import type { SavedEntity } from './SearchHistory/schema';
import { removeSearchHistory, useSearchHistoryStore } from './SearchHistory/useSearchHistoryStore';
import { addSearchHistory } from './SearchHistory/useSearchHistoryStore';
import { splitSlots } from './splitSlots';

/*#__PURE__*/ gql(/* GraphQL */ `
  query DefaultSearchBarResults {
    releases(
      pagination: { first: 3, sort: { createdAt: DESC } }
      filter: { releaseAlbumRevealStatus: ONLY_NOT_REVEALED_ALBUMS }
    ) {
      edges {
        node {
          ...SearchBarResultRelease
        }
      }
    }
    artists(pagination: { first: 3, sort: DESC }, filter: { hasRelease: true }) {
      edges {
        node {
          id
          ...SearchBarResultArtist
        }
      }
    }
    allCollectors(
      input: {
        filter: { includeArtists: false, onlyWithUsername: true }
        pagination: { first: 3, sort: { createdAt: DESC } }
      }
    ) {
      edges {
        node {
          id
          ...SearchBarResultCollector
        }
      }
    }
    allShelves(
      input: {
        filter: { type: [USER_CREATED] }
        pagination: { first: 3, sort: { createdAt: DESC } }
      }
    ) {
      edges {
        node {
          ...SearchBarResultShelf
        }
      }
    }
  }

  query InputSearchBarResults($input: SearchInput!) {
    search(input: $input) {
      id
      releasesPaginated {
        edges {
          node {
            ...SearchBarResultRelease
          }
        }
      }
      artistsPaginated {
        edges {
          node {
            ...SearchBarResultArtist
          }
        }
      }
      shelves(pagination: { first: 10 }) {
        edges {
          node {
            id
            ...SearchBarResultShelf
          }
        }
      }
      collectors(pagination: { first: 10 }) {
        edges {
          node {
            id
            ...SearchBarResultCollector
          }
        }
      }
    }
  }

  query SearchHistoryRelease($id: UUID!) {
    release(id: $id) {
      id
      ...SearchBarResultRelease
    }
  }

  query SearchHistoryArtist($id: UUID!) {
    artist(id: $id) {
      id
      ...SearchBarResultArtist
    }
  }

  query SearchHistoryPlaylist($id: UUID!) {
    shelf: playlistV2(id: $id) {
      id
      ...SearchBarResultShelf
    }
  }

  query SearchHistoryCollector($id: UUID!) {
    user(id: $id) {
      id
      ...SearchBarResultCollector
    }
  }

  fragment SearchBarResultRelease on Release {
    id
    title
    webappUri
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    artist {
      id
      name
    }
  }
  fragment SearchBarResultArtist on Artist {
    id
    name
    webappUri
    user {
      id
      publicAddress
      avatar {
        id
        url
      }
    }
  }
  fragment SearchBarResultShelf on Shelf {
    id
    name
    webappUri
    user {
      id
      username
      artist {
        id
      }
    }
    ...PlaylistCoverImages
  }
  fragment SearchBarResultCollector on User {
    id
    username
    publicAddress
    webappUri
    avatar {
      id
      url
    }
  }
`);

const slots = 16;
const SEARCH_RESULT_LIMIT = 5;

export function SearchResults({
  showSearchResults,
  onRowClick,
  onSearchClick,
}: {
  showSearchResults: boolean;
  onRowClick: () => void;
  onSearchClick: () => void;
}) {
  const { height } = useVisualViewport({
    delayDebounce: 0,
  });

  // text displayed is based on raw input, query is based on debounced input
  const { barRawInputText, debouncedSearchText } = useGlobalSearchBarInput();

  // default entries
  const { data: defaultResultsData, isInitialLoading: defaultResultsLoading } = useQuery(
    DefaultSearchBarResultsDocument,
    {
      enabled: showSearchResults,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  // results based on the debounced input
  const { data: searchResultsData, isInitialLoading: searchLoading } = useQuery(
    InputSearchBarResultsDocument,
    {
      variables: {
        input: {
          text: debouncedSearchText,
          limit: SEARCH_RESULT_LIMIT,
        },
      },
      staleTime: ms('1 minute'),
      enabled: showSearchResults && !!debouncedSearchText,
    },
  );

  const isEmptyInputText = !barRawInputText.length;

  const [artistResults, releaseResults, playlistResults, collectorResults] = useMemo(() => {
    // show default entries
    if (isEmptyInputText || !searchResultsData) {
      return [
        defaultResultsData?.data.artists.edges.map(value =>
          getFragment(SearchBarResultArtistFragmentDoc, value.node),
        ) ?? [],
        defaultResultsData?.data.releases.edges.map(value =>
          getFragment(SearchBarResultReleaseFragmentDoc, value.node),
        ) ?? [],
        defaultResultsData?.data.allShelves.edges.map(value =>
          getFragment(SearchBarResultShelfFragmentDoc, value.node),
        ) ?? [],
        defaultResultsData?.data.allCollectors.edges.map(value =>
          getFragment(SearchBarResultCollectorFragmentDoc, value.node),
        ) ?? [],
      ];
    }

    // show search results
    return [
      searchResultsData.data.search.artistsPaginated.edges.map(value =>
        getFragment(SearchBarResultArtistFragmentDoc, value.node),
      ),
      searchResultsData.data.search.releasesPaginated.edges.map(value =>
        getFragment(SearchBarResultReleaseFragmentDoc, value.node),
      ),
      searchResultsData.data.search.shelves.edges.map(value =>
        getFragment(SearchBarResultShelfFragmentDoc, value.node),
      ),
      searchResultsData.data.search.collectors.edges.map(value =>
        getFragment(SearchBarResultCollectorFragmentDoc, value.node),
      ),
    ];
  }, [
    defaultResultsData?.data.allCollectors.edges,
    defaultResultsData?.data.allShelves.edges,
    defaultResultsData?.data.artists.edges,
    defaultResultsData?.data.releases.edges,
    isEmptyInputText,
    searchResultsData,
  ]);

  const totalResults =
    releaseResults.length + artistResults.length + playlistResults.length + collectorResults.length;

  const isLoading = defaultResultsLoading || searchLoading;

  const history = useSearchHistoryStore();

  const results = React.useMemo(() => {
    const [historyResults, releases, artists, playlists, collectors] = splitSlots({
      slots,
      lists: [
        isEmptyInputText ? history : [],
        releaseResults,
        artistResults,
        playlistResults,
        collectorResults,
      ],
    });

    return {
      history: historyResults,
      releases,
      artists,
      playlists,
      collectors,
    };
  }, [isEmptyInputText, history, releaseResults, artistResults, playlistResults, collectorResults]);

  const resultsContent = React.useMemo(() => {
    return [
      results.history.length ? (
        <SearchRow key="history" header="Recent">
          {results.history.map(entity => {
            return <SearchBarHistoryRow key={entity.id} entity={entity} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.releases.length ? (
        <SearchRow key="releases" header="Releases">
          {results.releases.map(release => {
            return <ReleaseRow key={release.id} release={release} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.artists.length ? (
        <SearchRow key="artists" header="Artists">
          {results.artists.map(artist => {
            return <ArtistRow key={artist.id} artist={artist} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.playlists.length ? (
        <SearchRow key="playlists" header="Playlists">
          {results.playlists.map(playlist => {
            return <PlaylistRow key={playlist.id} playlist={playlist} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.collectors.length ? (
        <SearchRow key="collectors" header="Collectors">
          {results.collectors.map(collector => {
            return (
              <CollectorRow key={collector.id} collector={collector} onRowClick={onRowClick} />
            );
          })}
        </SearchRow>
      ) : null,
    ].filter((value): value is NonNullable<typeof value> => value != null);
  }, [
    onRowClick,
    results.artists,
    results.collectors,
    results.history,
    results.playlists,
    results.releases,
  ]);

  const shouldShowNoResults = !isLoading && totalResults === 0;

  return (
    <ScrollableContainer
      style={{
        zIndex: '$search',
        height: 'fit-content',
        maxHeight: shouldShowNoResults || !totalResults ? 'fit-content' : height - 85,
      }}
    >
      <SearchResultsContainer loadingState={isLoading}>
        {shouldShowNoResults ? (
          <SearchForRow searchText={barRawInputText} onSearchClick={onSearchClick} />
        ) : (
          <div className="mb-3">
            {barRawInputText !== '' && (
              <SearchForRow searchText={barRawInputText} onSearchClick={onSearchClick} />
            )}

            {isLoading ? (
              <LoadingContainer>
                <LoadingBars height={undefined} />
              </LoadingContainer>
            ) : (
              <>
                {resultsContent.map((value, index) => {
                  return index === 0 ? (
                    <div className="pt-2">{value}</div>
                  ) : (
                    <React.Fragment key={index}>
                      <DividerContainer>
                        <SearchDivider />
                      </DividerContainer>
                      {value}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
        )}
      </SearchResultsContainer>
    </ScrollableContainer>
  );
}

function SearchBarHistoryRow({
  entity,
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  switch (entity.type) {
    case 'Release': {
      return <SearchHistoryRelease entity={entity} onRowClick={onRowClick} />;
    }
    case 'Artist': {
      return <SearchHistoryArtist entity={entity} onRowClick={onRowClick} />;
    }
    case 'Playlist': {
      return <SearchHistoryPlaylist entity={entity} onRowClick={onRowClick} />;
    }
    case 'Collector': {
      return <SearchHistoryCollector entity={entity} onRowClick={onRowClick} />;
    }
    default: {
      const neverValue: never = entity.type;

      return null && neverValue;
    }
  }
}

function SearchHistoryRelease({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryReleaseDocument, {
    variables: {
      id,
    },

    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
    staleTime: ms('5 minutes'),
  });

  const release = getFragment(SearchBarResultReleaseFragmentDoc, data?.data.release);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!release) return null;

  return <ReleaseRow release={release} subtitle={type} onRowClick={onRowClick} />;
}

function SearchHistoryArtist({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryArtistDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  const artist = getFragment(SearchBarResultArtistFragmentDoc, data?.data.artist);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!artist) return null;

  return <ArtistRow artist={artist} subtitle={type} onRowClick={onRowClick} />;
}

function SearchHistoryPlaylist({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryPlaylistDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  const playlist = getFragment(SearchBarResultShelfFragmentDoc, data?.data.shelf);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!playlist) return null;

  return <PlaylistRow playlist={playlist} subtitle={type} onRowClick={onRowClick} />;
}

function SearchHistoryCollector({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryCollectorDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  const collector = getFragment(SearchBarResultCollectorFragmentDoc, data?.data.user);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!collector) return null;

  return <CollectorRow collector={collector} subtitle={type} onRowClick={onRowClick} />;
}

const SearchForRow = ({
  searchText,
  onSearchClick,
}: {
  searchText: string;
  onSearchClick: () => void;
}) => {
  return (
    <>
      {searchText !== '' && (
        <AccessibleLink
          pathnameUri={webappUriPath(`/search`)}
          onClick={ev => {
            ev.stopPropagation();
            ev.preventDefault();

            onSearchClick();

            getSearchPageResults({ inputText: searchText, useRouter: true });
          }}
        >
          <div className="bg-base50 pb-4 pt-4 hover:bg-white">
            <p className="pl-6 font-base text-base-m font-semibold hover:bg-white">
              Search for "{searchText.trim()}"
            </p>
          </div>
        </AccessibleLink>
      )}
    </>
  );
};

function PlaylistRow({
  playlist,
  subtitle,
  onRowClick,
}: {
  playlist: SearchBarResultShelfFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  const playlistName = playlist.name;

  const username = playlist.user.username;
  const isArtist = !!playlist.user.artist;

  return (
    <Link href={webappUriPath(playlist.webappUri)} key={playlist.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: playlist.id,
            name: playlistName,
            subText: username,
            type: 'Playlist',
          });
        }}
      >
        <ImageContainer overflow="hidden">
          <CoverImages playlist={playlist} maxSize={COVER_SIZE} priority={false} />
        </ImageContainer>

        <SearchResultTextContainer>
          <SearchResultText>{playlistName}</SearchResultText>
          {isArtist ? (
            <ArtistNameContainer>
              <SearchResultSubText>
                {subtitle ? `${subtitle}${subTextSeparator}${username}` : username}
              </SearchResultSubText>
              <VerifiedTooltip size={13.5} />
            </ArtistNameContainer>
          ) : (
            <SearchResultSubText>
              {subtitle ? `${subtitle}${subTextSeparator}${username}` : username}
            </SearchResultSubText>
          )}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}

function ReleaseRow({
  release,
  subtitle,
  onRowClick,
}: {
  release: SearchBarResultReleaseFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  const title = release.title;

  const artistName = release.artist.name;

  return (
    <Link href={webappUriPath(release.webappUri)} key={release.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: release.id,
            type: 'Release',
            name: title,
            subText: artistName,
          });
        }}
      >
        <ImageContainer>
          <Cover
            size={40}
            src={release.staticCoverImage?.url || release.coverImage.url}
            alt={release.title}
            borderRadius={6}
          />
        </ImageContainer>
        <SearchResultTextContainer>
          <SearchResultText>{title}</SearchResultText>
          {subtitle ? (
            <SearchResultSubText>
              {artistName ? `${subtitle}${subTextSeparator}${artistName}` : subtitle}
            </SearchResultSubText>
          ) : artistName ? (
            <SearchResultSubText>{artistName}</SearchResultSubText>
          ) : null}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}

function ArtistRow({
  artist,
  subtitle,
  onRowClick,
}: {
  artist: SearchBarResultArtistFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  if (!artist.name) return null;

  const name = artist.name;

  return (
    <Link href={webappUriPath(artist.webappUri)} key={artist.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: artist.id,
            name,
            type: 'Artist',
            subText: null,
          });
        }}
      >
        <Avatar
          size={COVER_SIZE}
          src={artist.user.avatar?.url}
          walletAddress={artist.user.publicAddress}
          disableLink
          borderRadius="50%"
          webappUri={artist.webappUri}
        />
        <SearchResultTextContainer>
          <ArtistNameContainer>
            <SearchResultText>{name}</SearchResultText>
            <VerifiedTooltip size={18} />
          </ArtistNameContainer>
          {subtitle ? <SearchResultSubText>{subtitle}</SearchResultSubText> : null}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}

function CollectorRow({
  collector,
  subtitle,
  onRowClick,
}: {
  collector: SearchBarResultCollectorFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  const username = collector.username;
  return (
    <Link href={webappUriPath(collector.webappUri)} key={collector.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: collector.id,
            name: collector.username,
            type: 'Collector',
            subText: null,
          });
        }}
      >
        <Avatar
          size={COVER_SIZE}
          src={collector.avatar?.url}
          walletAddress={collector.publicAddress}
          borderRadius="50%"
          webappUri={collector.webappUri}
        />
        <SearchResultTextContainer>
          <SearchResultText>{username}</SearchResultText>
          {subtitle ? <SearchResultSubText>{subtitle}</SearchResultSubText> : null}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}

const SearchResultRow = styled('a', {
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  gap: '.75rem',
  minHeight: '0.1rem',
  paddingTop: '0.355rem',
  paddingBottom: '0.355rem',

  paddingLeft: '1.125rem',
  paddingRight: '0.375rem',

  fontSize: '.75rem',
  color: 'black',
  fontWeight: '500',
  '&:before': {
    borderRadius: '$sm',
    margin: '0 0.65rem',
    position: 'absolute',
    top: 0,
    left: 0,
    width: 'stretch',
    height: '100%',
    display: 'block',
    content: '',
    zIndex: '$below',
  },
  '&:hover:before': {
    background: '$neutral100',
  },
});

const ImageContainer = styled('div', {
  position: 'relative',
  height: '40px',
  width: '40px',
  overflow: 'hidden',
  flexShrink: 0,
  borderRadius: 6,

  '-webkit-transform': 'translateZ(0)',

  variants: {
    overflow: {
      hidden: {
        overflow: 'hidden',
      },
    },
    imgBorderRadius: {
      sm: {
        img: {
          borderRadius: '$sm',
        },
      },
    },
  },
});

const ArtistNameContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '4px',
  alignItems: 'center',
});

const SearchResultTextContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  maxWidth: '45em',
  gap: '2px',
  overflow: 'hidden',
});

const SearchResultText = styled('span', {
  fontFamily: '$base',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  color: '$neutral800',
  lineHeight: '20px',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

const SearchResultSubText = styled('span', {
  fontFamily: '$base',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  color: '$neutral500',
  lineHeight: '14px',
  maxWidth: 'fit-content',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

const COVER_SIZE = 40;

const subTextSeparator = ' • ';
